import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { fetchBrands, imgUrl } from "../api";
import { useCategories } from "../hooks";
import { useSettings } from "../context/SettingsContext";

const AREA_RANGES = [
  { label: "Select the area size", value: "" },
  { label: "Under 1,000 sq ft", value: "0-999" },
  { label: "1,000 – 2,000 sq ft", value: "1000-1999" },
  { label: "2,000 – 4,000 sq ft", value: "2000-3999" },
  { label: "4,000 – 6,000 sq ft", value: "4000-5999" },
  { label: "6,000 – 8,000 sq ft", value: "6000-7999" },
  { label: "8,000 – 10,000 sq ft", value: "8000-9999" },
  { label: "10,000+ sq ft", value: "10000-999999" },
];

const INVESTMENT_RANGES = [
  { label: "Select a range", value: "" },
  { label: "Under ₹50 Lakh", value: "0-4999999" },
  { label: "₹50 Lakh – ₹1 Crore", value: "5000000-9999999" },
  { label: "₹1 Crore – ₹3 Crore", value: "10000000-29999999" },
  { label: "₹3 Crore – ₹5 Crore", value: "30000000-49999999" },
  { label: "₹5 Crore – ₹7 Crore", value: "50000000-69999999" },
  { label: "₹7 Crore – ₹10 Crore", value: "70000000-99999999" },
  { label: "Above ₹10 Crore", value: "100000000-999999999" },
];

// ─── Custom dropdown ──────────────────────────────────────────────────────────
function FilterDropdown({ label, value, onChange, options, isOpen, onToggle }) {
  const ref = useRef(null);
  const selected = options.find((o) => o.value === value) || options[0];

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onToggle(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [isOpen, onToggle]);

  return (
    <div
      ref={ref}
      className="relative flex flex-col flex-1 min-w-0 px-3 sm:px-5 py-2.5"
    >
      <button
        type="button"
        onClick={() => onToggle(!isOpen)}
        className="flex flex-col text-left w-full focus:outline-none"
      >
        <span className="font-body text-[9px] text-stone-400 tracking-[0.2em] uppercase mb-0.5">
          {label}
        </span>
        <div className="flex items-center justify-between gap-2">
          <span
            className={`font-body text-[13px] truncate transition-colors duration-200 ${value ? "text-stone-800" : "text-stone-400"}`}
          >
            {selected.label}
          </span>
          <svg
            className={`flex-shrink-0 text-stone-400 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`}
            width="11"
            height="11"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
          >
            <path d="M6 9l6 6 6-6" />
          </svg>
        </div>
      </button>

      <div
        className={`absolute left-0 right-0 top-full mt-2 z-50 bg-white border border-stone-200 rounded-2xl shadow-xl overflow-hidden transition-all duration-200 origin-top ${isOpen ? "opacity-100 scale-y-100 translate-y-0" : "opacity-0 scale-y-95 -translate-y-2 pointer-events-none"}`}
        style={{ minWidth: "200px" }}
      >
        {options.map((o, i) => (
          <button
            key={o.value}
            type="button"
            onClick={() => {
              onChange(o.value);
              onToggle(false);
            }}
            className={`w-full text-left px-4 py-3 font-body text-sm transition-colors duration-150 flex items-center justify-between
              ${i !== options.length - 1 ? "border-b border-stone-50" : ""}
              ${o.value === value ? "text-gold bg-amber-50/60" : "text-stone-700 hover:bg-stone-50 hover:text-stone-900"}`}
          >
            <span>{o.label}</span>
            {o.value === value && (
              <svg
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                className="text-gold flex-shrink-0"
              >
                <path d="M20 6L9 17l-5-5" />
              </svg>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Brand card ───────────────────────────────────────────────────────────────
function BrandCard({ brand }) {
  const navigate = useNavigate();
  return (
    <div
      className="group cursor-pointer select-none"
      onClick={() => navigate(`/brands/${brand.slug}`)}
    >
      <div
        className="relative overflow-hidden rounded-br-[40px] mb-3"
        style={{ aspectRatio: "3/4" }}
      >
        <img
          src={brand.heroImg ? imgUrl(brand.heroImg) : "images/french1.jpg"}
          alt={brand.name}
          draggable="false"
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/25 to-black/20" />
        <div className="absolute top-3 left-3">
          <span className="font-body text-white/90 text-[9px] md:text-[10px] tracking-[0.1em] uppercase bg-black/30 backdrop-blur-[2px] px-2.5 py-1 rounded-sm leading-none">
            {brand.category?.name || ""}
          </span>
        </div>
        <div className="absolute inset-0 bg-gray-700 opacity-20" />
        <div className="absolute inset-0 flex items-center justify-center px-3 z-10">
          {brand.logo ? (
            <img
              src={imgUrl(brand.logo)}
              alt={brand.name}
              className="max-w-[100%] h-auto object-contain filter brightness-110 drop-shadow-2xl"
            />
          ) : (
            <p className="font-display text-white text-xl font-light text-center">
              {brand.name}
            </p>
          )}
        </div>
        <div className="absolute inset-0 rounded-2xl border border-transparent group-hover:border-gold/50 transition-all duration-300" />
      </div>
      <p className="font-body text-stone-800 text-sm text-center leading-snug group-hover:text-gold transition-colors duration-200">
        {brand.name}
      </p>
    </div>
  );
}

// ─── Skeleton card ────────────────────────────────────────────────────────────
function BrandSkeleton() {
  return (
    <div className="animate-pulse">
      <div
        className="rounded-br-[40px] bg-stone-100 mb-3"
        style={{ aspectRatio: "3/4" }}
      />
      <div className="h-4 bg-stone-100 rounded mx-auto w-24" />
    </div>
  );
}

// ─── Pagination ───────────────────────────────────────────────────────────────
function Pagination({ current, total, onChange }) {
  if (total <= 1) return null;
  const getPages = () => {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    const pages = [],
      start = Math.max(1, current - 2),
      end = Math.min(total, current + 2);
    if (start > 1) {
      pages.push(1);
      if (start > 2) pages.push("…");
    }
    for (let i = start; i <= end; i++) pages.push(i);
    if (end < total) {
      if (end < total - 1) pages.push("…");
      pages.push(total);
    }
    return pages;
  };
  return (
    <div className="flex items-center justify-center gap-1 mt-12">
      <button
        onClick={() => onChange(current - 1)}
        disabled={current === 1}
        className="w-8 h-8 flex items-center justify-center text-stone-400 hover:text-gold disabled:opacity-25 disabled:cursor-not-allowed transition-colors"
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
        >
          <path d="M15 18l-6-6 6-6" />
        </svg>
      </button>
      {getPages().map((p, i) =>
        p === "…" ? (
          <span
            key={`el-${i}`}
            className="w-8 h-8 flex items-center justify-center text-stone-400 text-sm"
          >
            …
          </span>
        ) : (
          <button
            key={p}
            onClick={() => onChange(p)}
            className={`w-8 h-8 rounded-full font-body text-sm transition-all duration-200 ${p === current ? "bg-gold text-white shadow font-medium" : "text-stone-500 hover:text-stone-900"}`}
          >
            {p}
          </button>
        ),
      )}
      <button
        onClick={() => onChange(current + 1)}
        disabled={current === total}
        className="w-8 h-8 flex items-center justify-center text-stone-400 hover:text-gold disabled:opacity-25 disabled:cursor-not-allowed transition-colors"
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
        >
          <path d="M9 18l6-6-6-6" />
        </svg>
      </button>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function BrandsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { getBanner } = useSettings();
  const { categories, loading: catsLoading } = useCategories();

  const [brands, setBrands] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);

  // Read filters from URL
  const [category, setCategory] = useState(searchParams.get("category") || "");
  const [area, setArea] = useState(searchParams.get("area") || "");
  const [investment, setInvestment] = useState(
    searchParams.get("investment") || "",
  );
  const [page, setPage] = useState(parseInt(searchParams.get("page") || "1"));
  const [openDropdown, setOpenDropdown] = useState(null);

  const ITEMS_PER_PAGE = 8;

  // Category options for dropdown
  const CATEGORIES = [
    { label: "Select a category", value: "" },
    ...categories.map((c) => ({ label: c.name, value: c._id })),
  ];

  // Fetch brands from API
  useEffect(() => {
    setLoading(true);
    window.scrollTo({ top: 0 });

    const params = { page, limit: ITEMS_PER_PAGE };
    if (category) params.category = category;
    if (area) {
      const [min, max] = area.split("-");
      params.areaMin = min;
      params.areaMax = max;
    }
    if (investment) {
      const [min, max] = investment.split("-");
      params.investmentMin = min;
      params.investmentMax = max;
    }

    fetchBrands(params)
      .then((d) => {
        setBrands(d.data || []);
        setTotal(d.pagination?.total || 0);
        setTotalPages(d.pagination?.pages || 1);
      })
      .catch(() => setBrands([]))
      .finally(() => setLoading(false));
  }, [category, area, investment, page]);

  // Sync filters to URL
  useEffect(() => {
    const params = {};
    if (category) params.category = category;
    if (area) params.area = area;
    if (investment) params.investment = investment;
    if (page > 1) params.page = page;
    setSearchParams(params, { replace: true });
  }, [category, area, investment, page, setSearchParams]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [category, area, investment]);

  const hasFilters = !!(category || area || investment);
  const clearAll = () => {
    setCategory("");
    setArea("");
    setInvestment("");
  };

  const bannerSrc = getBanner("brands");

  return (
    <div className="min-h-screen bg-white pt-[60px]">
      {/* Banner */}
      <div className="relative h-44 sm:h-56 md:h-72 overflow-hidden">
        <img
          src={bannerSrc || "images/bg4.png"}
          alt="Brands & Franchises"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-black/50" />
        <div className="relative z-10 h-full flex items-center justify-center px-4">
          <h1
            className="font-display text-white font-light text-center"
            style={{ fontSize: "clamp(1.8rem, 5.5vw, 3.8rem)" }}
          >
            Brands &amp; Franchises
          </h1>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-8 md:px-10 lg:px-16 py-10 md:py-14">
        {/* Filter bar */}
        <div className="mb-8 md:mb-10">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center border border-stone-200 rounded-br-[40px] md:rounded-br-[30px] bg-white shadow-sm">
            <FilterDropdown
              label="Category"
              value={category}
              onChange={setCategory}
              options={CATEGORIES}
              isOpen={openDropdown === "category"}
              onToggle={(v) => setOpenDropdown(v ? "category" : null)}
            />
            <div className="hidden sm:block self-stretch w-px bg-stone-200 my-2" />
            <div className="sm:hidden h-px bg-stone-100 mx-4" />
            <FilterDropdown
              label="Area"
              value={area}
              onChange={setArea}
              options={AREA_RANGES}
              isOpen={openDropdown === "area"}
              onToggle={(v) => setOpenDropdown(v ? "area" : null)}
            />
            <div className="hidden sm:block self-stretch w-px bg-stone-200 my-2" />
            <div className="sm:hidden h-px bg-stone-100 mx-4" />
            <FilterDropdown
              label="Investment"
              value={investment}
              onChange={setInvestment}
              options={INVESTMENT_RANGES}
              isOpen={openDropdown === "investment"}
              onToggle={(v) => setOpenDropdown(v ? "investment" : null)}
            />
            <div className="flex items-center justify-end px-3 py-2 sm:py-0">
              <button
                aria-label="Search"
                className="w-10 h-10 rounded-full bg-gold hover:bg-stone-900 flex items-center justify-center text-white transition-colors duration-300 flex-shrink-0"
              >
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                >
                  <circle cx="11" cy="11" r="8" />
                  <path d="M21 21l-4.35-4.35" />
                </svg>
              </button>
            </div>
          </div>

          {/* Active filter pills */}
          {hasFilters && (
            <div className="flex flex-wrap items-center gap-2 mt-4 px-1">
              <span className="font-body text-stone-400 text-xs tracking-widest uppercase">
                Active:
              </span>
              {category && (
                <span className="inline-flex items-center gap-1.5 font-body text-xs bg-amber-50 text-gold border border-gold/30 px-3 py-1 rounded-full">
                  {CATEGORIES.find((c) => c.value === category)?.label}
                  <button
                    onClick={() => setCategory("")}
                    className="text-gold/60 hover:text-stone-900 font-bold leading-none"
                  >
                    ×
                  </button>
                </span>
              )}
              {area && (
                <span className="inline-flex items-center gap-1.5 font-body text-xs bg-amber-50 text-gold border border-gold/30 px-3 py-1 rounded-full">
                  {AREA_RANGES.find((r) => r.value === area)?.label}
                  <button
                    onClick={() => setArea("")}
                    className="text-gold/60 hover:text-stone-900 font-bold leading-none"
                  >
                    ×
                  </button>
                </span>
              )}
              {investment && (
                <span className="inline-flex items-center gap-1.5 font-body text-xs bg-amber-50 text-gold border border-gold/30 px-3 py-1 rounded-full">
                  {INVESTMENT_RANGES.find((r) => r.value === investment)?.label}
                  <button
                    onClick={() => setInvestment("")}
                    className="text-gold/60 hover:text-stone-900 font-bold leading-none"
                  >
                    ×
                  </button>
                </span>
              )}
              <button
                onClick={clearAll}
                className="font-body text-xs text-stone-400 hover:text-red-400 underline underline-offset-2 transition-colors"
              >
                Clear all
              </button>
            </div>
          )}
        </div>

        {/* Results count */}
        <div className="flex items-center justify-between mb-6 md:mb-8">
          <p className="font-body text-stone-500 text-sm">
            <span className="text-stone-900 font-medium">{total}</span> brand
            {total !== 1 ? "s" : ""}
            {hasFilters && " found"}
          </p>
          {total > 0 && (
            <p className="font-body text-stone-400 text-xs">
              Page {page} / {totalPages}
            </p>
          )}
        </div>

        {/* Brand grid */}
        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-8 md:gap-x-6 md:gap-y-10">
            {[...Array(8)].map((_, i) => (
              <BrandSkeleton key={i} />
            ))}
          </div>
        ) : brands.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-8 md:gap-x-6 md:gap-y-10">
            {brands.map((brand) => (
              <BrandCard key={brand._id} brand={brand} />
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <div className="w-16 h-16 rounded-full bg-stone-100 flex items-center justify-center mb-5">
              <svg
                width="26"
                height="26"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#C9A84C"
                strokeWidth="1.5"
              >
                <circle cx="11" cy="11" r="8" />
                <path d="M21 21l-4.35-4.35" />
              </svg>
            </div>
            <p className="font-display text-stone-400 text-2xl font-light mb-2">
              No brands found
            </p>
            <p className="font-body text-stone-400 text-sm mb-7">
              Try adjusting or clearing your filters
            </p>
            <button
              onClick={clearAll}
              className="font-body text-[11px] tracking-widest uppercase px-8 py-3 bg-gold hover:bg-stone-900 text-white rounded-full transition-all duration-200"
            >
              Clear Filters
            </button>
          </div>
        )}

        <Pagination
          current={page}
          total={totalPages}
          onChange={(p) => {
            setPage(p);
            window.scrollTo({ top: 0 });
          }}
        />
      </div>
    </div>
  );
}
